import { useRef, useEffect } from "react";
import { Container, Typography, Stack, Box, Button } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import numberToWords from "number-to-words";
import { useNavigate, useLocation } from "react-router";
import dayjs from "dayjs";
import QrCodeGenerator from "views/commonComponents/QrCode/QrCodeGenerator";
import { useSelector } from "react-redux";

// --- Modular Sub-Components (PrintHeader, PrintTable, PrintFooter remain as previously adjusted) ---

// PrintHeader Component
const PrintHeader = ({ data, typ, company, companyLogo, isQrShow, qrData }) => {
  return (
    <Stack direction="row" sx={{ height: "15.6rem", flexShrink: 0 }}>
      <div
        style={{
          width: "45%",
          borderRight: "1px solid black",
          height: "15.6rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          overflow: "hidden",
        }}
      >
        <div style={{ width: "13.3rem", height: "30%", margin: "1rem" }}>
          <img
            src={companyLogo}
            style={{
              objectFit: "contain",
              height: "100%",
              width: "100%",
            }}
            alt="Company Logo"
          />
        </div>

        <Container disableGutters sx={{ padding: "0.3rem", color: "black" }}>
          <Typography variant="h4">{company.Name}</Typography>
          <Typography>{company.Add1}</Typography>
          <Typography>{`${company.city_name}, ${company.Place}`}</Typography>
          <Typography> {company.Country}</Typography>

          {company.Postal && (
            <Typography> {`Zipcode :${company.Postal}`}</Typography>
          )}
          {company.Ph && <Typography>Ph : {company.Ph}</Typography>}
          {company.Add2 && <Typography>VAT: {company.Add2}</Typography>}
          <Typography> {company.Web}</Typography>
          <Typography> {company.EMail}</Typography>
        </Container>
      </div>
      <Stack direction="column" sx={{ width: "55%", height: "15.6rem" }}>
        <Typography align="center" variant="h4" sx={{ height: "1.5rem" }}>
          {typ === "SB" && "Tax Invoice "}
          {typ === "OF" && "Quotation"}
          {typ === "B2C" && "Tax Invoice "}
          {(typ === "SR" || typ === "SRB2C") && "Sales Return"}
          {typ === "DN" && "Delivery Note"}
        </Typography>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            borderColor: "black",
          }}
        >
          <tbody style={{ padding: "10px", borderColor: "black" }}>
            <tr>
              <td style={{ border: "1px solid black", height: "1.5rem" }}>
                <Typography>
                  {data.BillNo
                    ? typ === "OF"
                      ? `Quotation No: ${data.NewBillNo}`
                      : typ === "B2C"
                      ? `Invoice No: ${data.prefix}`
                      : `Invoice No: ${data.NewBillNo}`
                    : `Return No: ${data.RetNo}`}
                </Typography>
              </td>
            </tr>
            <tr>
              <td style={{ border: "1px solid black", height: "1.5rem" }}>
                <Typography>
                  {typ === "OF" ? `Date:` : ` Invoice Date:`}
                  <b>{dayjs(data.BillDate).format("DD-MM-YYYY")}</b>
                </Typography>
              </td>
            </tr>
            {typ !== "OF" && (
              <tr>
                <td style={{ border: "1px solid black" }}>
                  <Typography>
                    LPO No: <b>{data?.DCBillNo}</b>
                  </Typography>
                </td>
              </tr>
            )}
            {typ !== "OF" && (
              <tr>
                <td style={{ border: "1px solid black", height: "1.5rem" }}>
                  <Typography>
                    Quotation <b>{data.OrderNo}</b>
                  </Typography>
                </td>
              </tr>
            )}
            <tr>
              <td style={{ border: "1px solid black" }}>
                <Typography>
                  Client ID: <b>0</b>
                </Typography>
              </td>
            </tr>
          </tbody>
        </table>
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={0}
          sx={{ height: "100%", p: 1 }}
        >
          <Stack direction="column" alignItems={"start"} width={"80%"}>
            <Typography>
              <b style={{ display: "block" }}>To :{data.BillPartyName}</b>{" "}
              {data.CashP}
            </Typography>
            <Typography>{data.warranty || ""} </Typography>
            <Typography> </Typography>
            <Typography>
              <b>VAT NO:</b> {data.vat_no} &nbsp; <b>PH:</b> {data.Ph}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="center" width={"20%"}>
            <Box width={120}>
              {isQrShow ? (
                <QrCodeGenerator qrData={data.zatca_einv_qr_code || qrData} />
              ) : null}
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

// PrintTable Component
const PrintTable = ({
  sectionItems,
  decimalCount,
  tableHeaderHeight,
  baseRowHeight,
  additionalLineHeight,
  charPerLineEstimate,
  fixedTableParentHeight,
}) => {
  const getEstimatedRowHeight = (description) => {
    const descriptionLength = description?.length || 0;
    const lineCount = Math.max(
      1,
      Math.ceil(descriptionLength / charPerLineEstimate)
    );
    return baseRowHeight + (lineCount - 1) * additionalLineHeight;
  };

  const calculateTotalHeightOfRows = (items) => {
    return items.reduce(
      (acc, item) => acc + getEstimatedRowHeight(item.ItemNameTextField),
      0
    );
  };

  const estimatedTableContentHeight = calculateTotalHeightOfRows(sectionItems);
  const remainingHeight =
    fixedTableParentHeight - tableHeaderHeight - estimatedTableContentHeight;
  const numberOfFillerRows = Math.max(
    0,
    Math.floor(remainingHeight / baseRowHeight)
  );

  const fillerRows = Array.from({ length: numberOfFillerRows }).map((_, i) => (
    <tr key={`filler-${i}`} style={{ height: `${baseRowHeight}px` }}>
      <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
      <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
      <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
      <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
      <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
      <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
      <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
      <td style={{ borderRight: "1px solid black" }}>&nbsp;</td>
      <td>&nbsp;</td>
    </tr>
  ));

  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
      }}
    >
      <thead>
        <tr style={{ height: tableHeaderHeight }}>
          <th
            style={{
              textAlign: "left",
              border: "1px solid black",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            SI NO
          </th>
          <th
            style={{
              textAlign: "left",
              border: "1px solid black",
              width: "30%",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            Description
          </th>
          <th
            style={{
              textAlign: "center",
              border: "1px solid black",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            Qty
          </th>
          <th
            style={{
              textAlign: "center",
              border: "1px solid black",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            Rate
          </th>
          <th
            style={{
              textAlign: "center",
              border: "1px solid black",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            Unit
          </th>
          <th
            style={{
              textAlign: "center",
              border: "1px solid black",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            Amount
          </th>
          <th
            style={{
              textAlign: "center",
              border: "1px solid black",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            Tax %
          </th>
          <th
            style={{
              textAlign: "center",
              border: "1px solid black",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            Tax amt
          </th>
          <th
            style={{
              textAlign: "right",
              border: "1px solid black",
              padding: "0.2rem",
              fontSize: "12px",
            }}
          >
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {sectionItems.map((prod, index) => (
          <tr
            key={prod.id || `item-${index}`}
            style={{ height: getEstimatedRowHeight(prod.ItemNameTextField) }}
          >
            <td
              style={{
                textAlign: "center",
                verticalAlign: "top",
                borderRight: "1px solid black",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {prod.id}
            </td>
            <td
              style={{
                textAlign: "left",
                verticalAlign: "top",
                borderRight: "1px solid black",
                paddingLeft: "5px",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {prod.ItemNameTextField}
            </td>
            <td
              style={{
                textAlign: "center",
                verticalAlign: "top",
                borderRight: "1px solid black",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {prod.qty}
            </td>
            <td
              style={{
                textAlign: "center",
                verticalAlign: "top",
                borderRight: "1px solid black",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {parseFloat(prod.Rate1).toFixed(decimalCount)}
            </td>
            <td
              style={{
                textAlign: "center",
                verticalAlign: "top",
                borderRight: "1px solid black",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {prod.Typ}
            </td>
            <td
              style={{
                textAlign: "center",
                verticalAlign: "top",
                borderRight: "1px solid black",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {parseFloat(prod.Rate1 * prod.qty).toFixed(decimalCount)}
            </td>
            <td
              style={{
                textAlign: "center",
                verticalAlign: "top",
                borderRight: "1px solid black",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {prod.Tax1}
            </td>
            <td
              style={{
                textAlign: "center",
                verticalAlign: "top",
                borderRight: "1px solid black",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {parseFloat(prod.taxAmt || 0).toFixed(decimalCount)}
            </td>
            <td
              style={{
                textAlign: "center",
                verticalAlign: "top",
                borderRight: "1px solid black",
                fontSize: "12px",
                padding: "0.2rem",
              }}
            >
              {parseFloat(prod.total).toFixed(decimalCount)}
            </td>
          </tr>
        ))}
        {fillerRows}
      </tbody>
    </table>
  );
};

// PrintFooter Component
const PrintFooter = ({
  result,
  company,
  data,
  typ,
  commonState,
  decimalCount,
  tableData,
}) => {
  return (
    <Stack
      direction="row"
      sx={{
        flexShrink: 0,
        flexGrow: 1,
        display: "flex",
        borderTop: "1px solid black",
      }}
    >
      {/* Left Column (60% width) */}
      <Stack
        direction="column"
        sx={{
          width: "60%",
          borderRight: "1px solid black",
          flexGrow: 1,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Container disableGutters sx={{ padding: "0.3rem", flexShrink: 0 }}>
          <Typography fontSize={"13px"}> Amount In Words :</Typography>
          <Typography
            variant="h5"
            sx={{ textTransform: "capitalize", fontSize: "13px" }}
          >
            {result}
          </Typography>
        </Container>
        <Container
          disableGutters
          sx={{
            borderTop: "1px solid black",
            padding: "0.3rem",
            flexShrink: 0,
          }}
        >
          <Typography> Bank Details:-</Typography>
          <Typography variant="h5"> Bank Name: {company.bank}</Typography>
          <Typography variant="h5"> A/c Name: {company.beneficiary}</Typography>
          <Typography variant="h5"> A/c No: {company.account_no}</Typography>
          <Typography variant="h5"> Branch: {company.branch}</Typography>
          <Typography variant="h5"> IBAN: {company.ifsc_code}</Typography>
        </Container>
        {/* Declaration Container - This will now take most of the remaining space in the left column */}
        <Container
          disableGutters
          sx={{
            borderTop: "1px solid black",
            padding: "5px",
            overflow: "hidden",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            // justifyContent: "flex-end",
          }}
        >
          <div
            style={{ whiteWhiteSpace: "normal", overflow: "hidden" }}
            dangerouslySetInnerHTML={{
              __html:
                typ === "OF"
                  ? commonState.orderFormDeclaration
                  : commonState.salesBillDeclaration,
            }}
          />
        </Container>
      </Stack>

      {/* Right Column (40% width) */}
      <Stack
        direction="column"
        sx={{
          width: "40%",
          flexGrow: 1,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* Fixed height total stacks */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            height: "2rem",
            paddingLeft: "5px",
            paddingRight: "5px",
            flexShrink: 0,
          }}
        >
          <Typography variant="h5">Subtotal :</Typography>
          <Typography variant="h5">
            {" "}
            {tableData
              .reduce(
                (accumulator, currentValue) =>
                  accumulator + currentValue.Rate1 * currentValue.qty,
                0
              )
              .toFixed(decimalCount)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderTop: "1px solid black",
            height: "2rem",
            paddingLeft: "5px",
            paddingRight: "5px",
            flexShrink: 0,
          }}
        >
          <Typography variant="h5">
            {" "}
            Tax Amount {data.BillTaxField > 0
              ? `(${data.BillTaxField}%)`
              : ""}:{" "}
          </Typography>
          <Typography variant="h5">
            {" "}
            {(typ !== "SR" || typ !== "SRB2C"
              ? parseFloat(data?.TItTaxAmt + data?.TaxAmt)
              : parseFloat(data.TaxAmt)
            ).toFixed(decimalCount)}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderTop: "1px solid black",
            height: "2rem",
            paddingLeft: "5px",
            paddingRight: "5px",
            flexShrink: 0,
          }}
        >
          <Typography variant="h5"> Discount : </Typography>
          <Typography variant="h5">
            {parseFloat(data.TotDiscPercAmt + data.BillDiscAmtField).toFixed(
              decimalCount
            )}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            borderBlock: "1px solid black",
            height: "2rem",
            paddingLeft: "5px",
            paddingRight: "5px",
            flexShrink: 0,
          }}
        >
          <Typography variant="h5"> Net Total : </Typography>
          <Typography variant="h5">
            {" "}
            {parseFloat(data.BillNetTotalField).toFixed(decimalCount)}
          </Typography>
        </Stack>
        {/* Signatory section - now flexGrow to take remaining space */}
        <Stack
          direction="column"
          justifyContent="space-between"
          sx={{
            paddingLeft: "5px",
            paddingRight: "5px",
            flexGrow: 1,
          }}
        >
          <Typography align="center" variant="h6" sx={{ marginLeft: "auto" }}>
            E&OE
          </Typography>
          <Typography align="center" variant="h6">
            For {company.Name}
          </Typography>
          <Typography align="center" variant="h6">
            Authorised Signatory
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

// --- Main A4PrintTwo Component (Updated Constants) ---

const A4PrintTwo = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    data = {},
    tableData = [],
    qrData,
    typ,
    navigateBack = "/",
  } = location.state || {};

  const company = useSelector((state) => state.common.companyInfo) || {};
  const companyLogo = company.logo ? JSON.parse(company.logo)?.img : null;
  const decimalCount = useSelector((state) => state.common.decimalCount) || 2;
  const primaryCurrency =
    useSelector((state) => state.common.primaryCurrency) || "Rupees";
  const subCurrency =
    useSelector((state) => state.common.subCurrency) || "Paise";
  const isQrShow = useSelector((state) => state.common.QrShowPrint);
  const commonState = useSelector((state) => state.common) || {};

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@page { size: A4; margin: 0; } body { margin: 0; }`,
    onAfterPrint: () => {
      navigate(navigateBack);
    },
  });

  useEffect(() => {
    if (company && Object.keys(company).length > 0) {
      const timeoutId = setTimeout(() => {
        handlePrint();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [company, handlePrint]);

  function convertNumberToWordsWithCurrency(number) {
    if (isNaN(number) || number === null) {
      return `Zero ${primaryCurrency} and Zero ${subCurrency}`;
    }
    const totalAmount = parseFloat(number);
    const wholeAmount = Math.floor(totalAmount);
    const fractionalAmount = Math.round((totalAmount - wholeAmount) * 100);

    const words = numberToWords.toWords(wholeAmount);
    const fractionalWords = numberToWords.toWords(fractionalAmount);

    let result = `${words} ${primaryCurrency}`;
    if (fractionalAmount > 0) {
      result += ` and ${fractionalWords} ${subCurrency}`;
    }
    return result;
  }

  const amount = data.BillNetTotalField;
  const result = convertNumberToWordsWithCurrency(amount);

  // --- CRITICAL CONSTANTS FOR PAGE LAYOUT (MODIFIED) ---
  const A4_HEIGHT_PX = 1123;
  const A4_WIDTH_PX = 794;

  // const PAGE_OUTER_PADDING_PX = 5;
  const MAIN_CONTENT_WRAPPER_BORDER_WIDTH = 1;

  const TABLE_HEADER_ROW_HEIGHT_PX = 30;
  // MODIFIED: Increased base row height for better fit
  const BASE_ROW_HEIGHT_PX = 20;
  // MODIFIED: Increased additional line height for wrapped text
  const ADDITIONAL_LINE_HEIGHT_PX = 12;
  const CHARS_PER_LINE_DESCRIPTION = 30;

  const DESIRED_NUMBER_OF_ROWS = 18;

  // FIXED_TABLE_HEIGHT_PX will automatically be larger due to new BASE_ROW_HEIGHT_PX
  const FIXED_TABLE_HEIGHT_PX =
    TABLE_HEADER_ROW_HEIGHT_PX + DESIRED_NUMBER_OF_ROWS * BASE_ROW_HEIGHT_PX;

  const AVAILABLE_HEIGHT_FOR_TABLE_ROWS =
    FIXED_TABLE_HEIGHT_PX - TABLE_HEADER_ROW_HEIGHT_PX;

  const getEstimatedRowHeight = (description) => {
    const descriptionLength = description?.length || 0;
    const lineCount = Math.max(
      1,
      Math.ceil(descriptionLength / CHARS_PER_LINE_DESCRIPTION)
    );
    return BASE_ROW_HEIGHT_PX + (lineCount - 1) * ADDITIONAL_LINE_HEIGHT_PX;
  };

  const generateSections = (tableData) => {
    const sections = [];
    let currentIndex = 0;
    const dataWithIds = tableData.map((item, index) => ({
      ...item,
      id: index + 1,
    }));

    while (currentIndex < dataWithIds.length) {
      let currentSectionItems = [];
      let currentSectionHeightOccupied = 0;

      for (let i = currentIndex; i < dataWithIds.length; i++) {
        const item = dataWithIds[i];
        const itemHeight = getEstimatedRowHeight(item.ItemNameTextField);

        if (
          currentSectionHeightOccupied + itemHeight <=
          AVAILABLE_HEIGHT_FOR_TABLE_ROWS
        ) {
          currentSectionHeightOccupied += itemHeight;
          currentSectionItems.push(item);
        } else {
          if (currentSectionItems.length === 0) {
            currentSectionItems.push(item);
            currentSectionHeightOccupied += itemHeight;
          }
          break;
        }
      }

      sections.push(currentSectionItems);
      currentIndex += currentSectionItems.length;
    }

    if (sections.length === 0 && tableData.length === 0) {
      sections.push([]);
    }
    return sections;
  };

  const sectionsData = generateSections(tableData);
  const totalPages = sectionsData.length;

  const renderSection = (sectionItems, sectionIndex) => {
    const isLastPage = sectionIndex === totalPages - 1;

    return (
      <div
        key={`page-${sectionIndex}`}
        style={{
          width: `${A4_WIDTH_PX}px`,
          height: `${A4_HEIGHT_PX}px`,
          boxSizing: "border-box",
          // padding: `${PAGE_OUTER_PADDING_PX}px`,
          padding: `20px`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          pageBreakAfter: isLastPage ? "auto" : "always",
          color: "black",
          backgroundColor: "white",
        }}
      >
        <div
          style={{
            border: `${MAIN_CONTENT_WRAPPER_BORDER_WIDTH}px solid black`,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <PrintHeader
            data={data}
            typ={typ}
            company={company}
            companyLogo={companyLogo}
            isQrShow={isQrShow}
            qrData={qrData}
          />

          <PrintTable
            sectionItems={sectionItems}
            decimalCount={decimalCount}
            tableHeaderHeight={TABLE_HEADER_ROW_HEIGHT_PX}
            baseRowHeight={BASE_ROW_HEIGHT_PX}
            additionalLineHeight={ADDITIONAL_LINE_HEIGHT_PX}
            charPerLineEstimate={CHARS_PER_LINE_DESCRIPTION}
            fixedTableParentHeight={FIXED_TABLE_HEIGHT_PX}
          />

          <PrintFooter
            result={result}
            company={company}
            data={data}
            typ={typ}
            commonState={commonState}
            decimalCount={decimalCount}
            tableData={tableData}
          />
        </div>
      </div>
    );
  };

  return (
    <div>
      <Button onClick={handlePrint}>Print</Button>
      <div ref={componentRef}>
        {sectionsData.map((sectionItems, index) =>
          renderSection(sectionItems, index)
        )}
      </div>
    </div>
  );
};

export default A4PrintTwo;
